<template>
  <v-card outlined id="fullPageRadio" tabindex="0">
    <v-toolbar flat>
      <v-toolbar-title class="text-h6">Landing Page Options</v-toolbar-title>
    </v-toolbar>
    <v-card-text class="px-6 py-0">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-radio-group v-model="formData.selectedOption" column class="pt-0 mt-0" @change="coverOptionChange">
          <v-radio label="Default Cover" :value="CoverOption.Default"></v-radio>
          <v-radio label="Custom Cover" :value="CoverOption.Custom"></v-radio>
        </v-radio-group>
        <div v-if="formData.selectedOption !== CoverOption.Default">
          <label class="grey--text text--darken-1">Cover Media</label>
          <Upload
            :value="value.media"
            @input="setMedia"
            :clientId="0"
            :showErrorMessage="formData.selectedOption === CoverOption.Custom && fileExtension === 'pdf'"
            :errorMessage="'Text functionality cannot be used with a PDF file. Please upload another file type in order to add text.'"
            class="mt-2 mb-6"
            @validateForm="validate"
          />
          <div v-if="formData.media?.url" class="mb-2">
            <label>Media Position</label>
            <v-radio-group v-model="formData.media.position" column class="mt-1">
              <div class="media-radio-option">
                <v-radio label="Center" :value="MediaPosition.Center"></v-radio>
                <div>
                  <v-tooltip right nudge-bottom="20" max-width="30em">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="primary" small dark v-bind="attrs" v-on="on"> mdi-information-outline </v-icon>
                    </template>
                    <span ref="centerSpan" id="center-span">
                      Centers media in container respecting the aspect-ratio
                    </span>
                  </v-tooltip>
                </div>
              </div>
              <div class="media-radio-option">
                <v-radio label="Cover" :value="MediaPosition.Cover"></v-radio>
                <div>
                  <v-tooltip right nudge-bottom="30" max-width="30em">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="primary" small dark v-bind="attrs" v-on="on"> mdi-information-outline </v-icon>
                    </template>
                    <span ref="coverSpan">Cover the media in the container (may result in loss of content)</span>
                  </v-tooltip>
                </div>
              </div>
            </v-radio-group>
          </div>

          <label>Alt Text</label>
          <v-text-field
            class="mt-2"
            autofocus
            autocomplete="off"
            dense
            name="text-field"
            outlined
            v-model="formData.media.alt"
            required
            :rules="altRules"
          ></v-text-field>

          <div v-if="fileType === 'image'">
            <label>Image URL Redirect Link</label>
            <v-text-field
              class="mt-2"
              autocomplete="off"
              dense
              name="text-field"
              outlined
              v-model="redirectLink"
            ></v-text-field>
          </div>

          <div class="d-flex flex-column align-stretch gap-4">
            <HtmlField
              v-model="htmlBlock"
              :config="{ label: 'Landing Page Text', title: 'Landing Page Body Text', hasAnchorPosition: true }"
              :selectedAnchorPosition="anchorPosition"
              :disabled="fileExtension === 'pdf'"
              @setAnchorPosition="setAnchorPosition"
            />
          </div>
        </div>
      </v-form>
    </v-card-text>
  </v-card>
</template>
<script lang="ts">
  import { HtmlField } from '@/components/Builder';
  //import UploadImage from '@/components/Ui/UploadImage.vue';

  import Vue, { nextTick, PropType } from 'vue';
  import Upload from '../Ui/Upload.vue';
  import { AnchorPosition, CoverOption, LandingPageData, MediaPosition, MediaType } from '../types';
  import { ImageExts, VideoExts } from '@/constants';

  const LandingPage = Vue.extend({
    name: 'LandingPage',
    components: {
      //UploadImage,
      Upload,
      HtmlField,
    },
    props: {
      value: {
        type: Object as PropType<LandingPageData>,
        required: true,
      },
      clientId: {
        type: Number,
        required: false,
      },
      isLoading: {
        type: Boolean,
        required: false,
      },
    },
    data() {
      return {
        CoverOption,
        MediaPosition,
        valid: true,
        alertVisible: true,
        altRules: [
          (v: string) => {
            if (!this.$data.formData.media.url) {
              return true;
            }
            return !!v || 'Alt Text is required';
          },
        ],
        htmlBlock: {
          html: this.value.content,
        },
        anchorPosition: this.value.anchorPosition,
        formData: {
          media: {
            ...this.value.media,
            alt: this.value.media?.alt ?? '',
            url: this.value.media?.url ?? '',
            position: this.value.media?.position ?? MediaPosition.Center,
          },
          content: this.value.content,
          anchorPosition: this.value.anchorPosition,
          selectedOption: this.value.selectedOption ?? CoverOption.Default,
        },
        redirectLink: '',
      };
    },
    methods: {
      validate() {
        return this.form.validate();
      },
      setAnchorPosition(newValue: any) {
        this.anchorPosition = newValue;
        this.formData.anchorPosition = newValue;
      },
      setMedia(newValue: any) {
        this.formData.media = { ...this.formData.media, ...newValue };
      },
      coverOptionChange(val: CoverOption) {
        if (val === CoverOption.Custom) {
          this.formData.media = {
            ...this.formData.media,
            alt: this.value.media?.alt ?? '',
            url: this.value.media?.url ?? '',
            position: this.value.media?.position ?? MediaPosition.Center,
          };
          this.formData.content = this.formData.content || '';
          this.formData.anchorPosition = this.formData.anchorPosition ?? AnchorPosition.MiddleCenter;
        }
      },
    },
    computed: {
      form(): VForm {
        return this.$refs.form as VForm;
      },
      fileExtension(): string {
        return this.formData?.media?.url?.split('.').pop();
      },
      fileType(): MediaType {
        if (VideoExts.includes(this.fileExtension)) {
          return 'video';
        }
        if (ImageExts.includes(this.fileExtension)) {
          return 'image';
        }
        if (this.fileExtension === 'pdf') {
          return 'pdf';
        }

        return '';
      },
    },
    watch: {
      formData: {
        handler: function (data) {
          this.$emit('input', data);
        },
        deep: true,
      },
      'htmlBlock.html': {
        handler(newValue) {
          this.formData.content = newValue;
          if (!newValue) {
            this.anchorPosition = undefined;
          }
        },
        immediate: true,
      },
      redirectLink(newVal) {
        this.formData.media.imageTypeDetails = { ...this.formData.media.imageTypeDetails, redirectLink: newVal };
      },
    },
  });
  export type LandingPageRef = InstanceType<typeof LandingPage>;
  export default LandingPage;
  export type VForm = Vue & { validate: () => boolean };
</script>

<style scoped>
  .media-radio-option {
    display: flex;
    gap: 4px;
  }
</style>
